import React, { useEffect, useState } from "react";
import { MailQuestion, CheckCircle2, Circle, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const shortDate = (iso) => { const d = new Date(iso || ""); return Number.isNaN(d.getTime()) ? "—" : `${d.getDate()} ${MONTHS[d.getMonth()]}`; };

function EnquiryRow({ e, onToggle }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/supplier/enquiries/${e.id}/replied`); onToggle(e.id, data.replied_at); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not update"); }
    finally { setBusy(false); }
  };
  return (
    <li className="py-3 flex flex-col sm:flex-row sm:items-start gap-3" data-testid={`supplier-enquiry-${e.id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#11211A]"><span className="font-medium">{e.name}</span>{e.company && <span className="text-[#7D8C85]"> · {e.company}</span>} <span className="text-[11px] text-[#9AA6A0] ml-1">{shortDate(e.created_at)}</span></div>
        <a href={`mailto:${e.email}`} className="text-xs text-[#264839] hover:underline" data-testid={`supplier-enquiry-email-${e.id}`}>{e.email}</a>
        <p className="text-xs text-[#4A5550] mt-1 whitespace-pre-wrap">{e.message}</p>
      </div>
      <button type="button" onClick={toggle} disabled={busy} className={`shrink-0 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1.5 border transition ${e.replied_at ? "bg-[#264839] border-[#264839] text-white" : "border-[#C4CFC9] text-[#4A5550] hover:border-[#264839] hover:text-[#264839]"}`} title={e.replied_at ? "Click to mark as not replied" : "Mark this enquiry as replied"} data-testid={`supplier-enquiry-replied-${e.id}`}>
        {e.replied_at ? <><CheckCircle2 className="w-3.5 h-3.5" /> Replied</> : <><Circle className="w-3.5 h-3.5" /> Mark as Replied</>}
      </button>
    </li>
  );
}

export default function EnquiryCountBadge() {
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    const load = () => Promise.all([api.get("/supplier/enquiries/summary"), api.get("/supplier/enquiries")])
      .then(([s, l]) => { if (alive) { setData(s.data); setItems(l.data.items || []); } }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("focus", load);
    return () => { alive = false; clearInterval(t); window.removeEventListener("focus", load); };
  }, []);
  if (!data) return null;
  const n = data.this_month;
  const month = data.month_label.split(" ")[0];
  const unreplied = items.filter((e) => !e.replied_at).length;
  const onToggle = (id, replied_at) => setItems((xs) => xs.map((e) => (e.id === id ? { ...e, replied_at } : e)));
  return (
    <div className={`mb-4 border ${n > 0 ? "border-[#264839]/30 bg-[#E8ECE9]/60" : "border-[#E8ECE9] bg-white"}`} data-testid="enquiry-count-badge">
      <div className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-9 h-9 rounded-full bg-[#264839] text-white flex items-center justify-center shrink-0"><MailQuestion className="w-4 h-4" /></div>
          <div>
            <div className="text-sm text-[#11211A]" data-testid="enquiry-count-text">
              {n > 0
                ? <>You have received <strong data-testid="enquiry-count-number">{n}</strong> hotelier enquir{n === 1 ? "y" : "ies"} in {month}</>
                : <>No hotelier enquiries yet in {month} — <strong>{data.total}</strong> all-time</>}
            </div>
            <div className="text-[11px] text-[#7D8C85]">
              {data.last_month > 0 ? `${data.last_month} last month · ` : ""}{unreplied > 0 ? <span className="text-[#B35C4A] font-medium">{unreplied} awaiting your reply</span> : items.length > 0 ? "All replied — nice work." : "Enquiries reach you by email via Accommodation Suppliers."}
            </div>
          </div>
        </div>
        {items.length > 0 && (
          <button type="button" onClick={() => setOpen((o) => !o)} className="btn-ghost text-xs shrink-0" data-testid="enquiry-list-toggle">
            {open ? <><ChevronUp className="w-3.5 h-3.5" /> Hide enquiries</> : <><ChevronDown className="w-3.5 h-3.5" /> View enquiries ({items.length})</>}
          </button>
        )}
      </div>
      {open && (
        <ul className="px-5 pb-3 divide-y divide-[#E8ECE9] border-t border-[#E8ECE9]" data-testid="supplier-enquiry-list">
          {items.map((e) => <EnquiryRow key={e.id} e={e} onToggle={onToggle} />)}
        </ul>
      )}
    </div>
  );
}
